import React, { useState, useEffect } from 'react';
import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';
import PaymentGate from './PaymentGate';
import '../styles/design-system.css';

export default function AIMarketingAgent() {
  const [userProfile, setUserProfile] = useState(null);
  const [userSubscriptionStatus, setUserSubscriptionStatus] = useState('free');
  const [conversionStage, setConversionStage] = useState('awareness');
  const [userBehavior, setUserBehavior] = useState({
    pagesVisited: [],
    timeOnSite: 0,
    personalityTestCompleted: false,
    localMeetsViewed: 0,
    messagesAttempted: 0,
    upgradeAttempts: 0
  });
  const [showPersonalizedOffer, setShowPersonalizedOffer] = useState(false);
  const [currentOffer, setCurrentOffer] = useState(null);

  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const currentUser = await getCurrentUser();
        const attributes = await fetchUserAttributes();
        
        const subscriptionStatus = attributes['custom:subscriptionStatus'] || 'free';
        const personalityTestCompleted = attributes['custom:personalityTestCompleted'] === 'true';
        
        setUserProfile({
          id: currentUser.userId,
          name: attributes.name || 'User',
          email: attributes.email,
          personalityType: attributes['custom:personalityType'],
          personalityScore: attributes['custom:personalityScore'],
          joinDate: attributes['custom:joinDate'] || new Date().toISOString()
        });
        
        setUserSubscriptionStatus(subscriptionStatus);
        
        // Analyze user behavior for conversion optimization
        analyzeUserBehavior(subscriptionStatus, personalityTestCompleted);
      } catch (error) {
        console.error('Error loading user profile:', error);
      }
    };

    loadUserProfile();
  }, []);

  const analyzeUserBehavior = (subscriptionStatus, personalityTestCompleted) => {
    // This would integrate with analytics to track user behavior
    const behavior = {
      pagesVisited: ['home', 'local-meets', 'profile'],
      timeOnSite: 15, // minutes
      personalityTestCompleted,
      localMeetsViewed: 3,
      messagesAttempted: 1,
      upgradeAttempts: 0
    };
    
    setUserBehavior(behavior);
    
    // Determine conversion stage
    if (subscriptionStatus === 'paid') {
      setConversionStage('retention');
    } else if (personalityTestCompleted && behavior.localMeetsViewed > 2) {
      setConversionStage('consideration');
    } else if (personalityTestCompleted) {
      setConversionStage('interest');
    } else {
      setConversionStage('awareness');
    }
  };

  const generatePersonalizedOffer = () => {
    const offers = {
      awareness: {
        title: 'Discover Your Travel Personality',
        message: 'Take our personality test to find compatible travel companions!',
        cta: 'Start Free Test',
        benefits: [
          'Find travelers who match your style',
          'See personality compatibility scores',
          'Get personalized travel recommendations'
        ],
        urgency: 'Join thousands of travelers who trust our matching system'
      },
      interest: {
        title: 'Ready to Meet Travel Companions?',
        message: 'You\'ve completed your personality test! Now upgrade to connect with compatible travelers.',
        cta: 'Upgrade to Connect',
        benefits: [
          'Message compatible travelers directly',
          'See detailed personality match scores',
          'Join local meetups and travel groups',
          'Organize your own events'
        ],
        urgency: 'Limited time: 50% off your first month!'
      },
      consideration: {
        title: 'You\'re Almost There!',
        message: 'You\'ve shown great interest in our community. Upgrade now to unlock all features.',
        cta: 'Unlock Full Access',
        benefits: [
          'Unlimited messaging with travelers',
          'Full access to local meetups',
          'Create and organize events',
          'Priority support and features'
        ],
        urgency: 'Special offer: 30-day free trial, then just £9.99/month'
      },
      retention: {
        title: 'Welcome Back!',
        message: 'Thanks for being a valued member. Here are some exclusive features just for you.',
        cta: 'Explore Premium Features',
        benefits: [
          'Exclusive travel group access',
          'Advanced personality matching',
          'Priority event notifications',
          'Premium organizer tools'
        ],
        urgency: 'New features added monthly for our premium members'
      }
    };

    return offers[conversionStage] || offers.awareness;
  };

  const handleOfferClick = () => {
    const offer = generatePersonalizedOffer();
    
    if (conversionStage === 'awareness') {
      // Navigate to personality test
      window.location.href = '/personality-test';
    } else if (conversionStage === 'retention') {
      // Show premium features
      setShowPersonalizedOffer(true);
      setCurrentOffer(offer);
    } else {
      // Navigate to upgrade page
      window.location.href = '/pricing';
    }
  };

  const renderConversionMessage = () => {
    const offer = generatePersonalizedOffer();
    const isPaidUser = userSubscriptionStatus === 'paid' || userSubscriptionStatus === 'premium';

    if (isPaidUser) {
      return null; // Don't show conversion messages to paid users
    }

    return (
      <div className="bg-gradient-to-r from-primary to-primary-dark text-white rounded-xl p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <span className="text-xl">🎯</span>
            </div>
          </div>
          
          <div className="flex-1">
            <h3 className="text-xl font-bold mb-2">{offer.title}</h3>
            <p className="text-white/90 mb-4">{offer.message}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {offer.benefits.map((benefit, index) => (
                <div key={index} className="flex items-start">
                  <span className="text-success mr-2">✓</span>
                  <span className="text-sm text-white/90">{benefit}</span>
                </div>
              ))}
            </div>
            
            <div className="bg-white/10 rounded-lg p-3 mb-4">
              <p className="text-sm font-medium">{offer.urgency}</p>
            </div>
            
            <button
              onClick={handleOfferClick}
              className="btn btn-secondary text-lg px-6 py-3"
            >
              {offer.cta}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderBehavioralInsights = () => {
    if (userSubscriptionStatus === 'paid' || userSubscriptionStatus === 'premium') {
      return (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
              <span className="text-white text-sm">✓</span>
            </div>
            <div>
              <h4 className="font-semibold text-green-900">Premium Member Benefits</h4>
              <p className="text-sm text-green-700">
                You have full access to all features. Here are some ways to maximize your experience:
              </p>
            </div>
          </div>
          
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-3 bg-white rounded-lg">
              <div className="text-2xl mb-2">👥</div>
              <h5 className="font-medium text-sm">Organize Events</h5>
              <p className="text-xs text-gray-600">Create local meetups and travel groups</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg">
              <div className="text-2xl mb-2">💬</div>
              <h5 className="font-medium text-sm">Unlimited Messaging</h5>
              <p className="text-xs text-gray-600">Connect with any member directly</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg">
              <div className="text-2xl mb-2">🔍</div>
              <h5 className="font-medium text-sm">Advanced Search</h5>
              <p className="text-xs text-gray-600">Find perfect travel companions</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
            <span className="text-white text-sm">💡</span>
          </div>
          <div>
            <h4 className="font-semibold text-blue-900">Personalized Recommendations</h4>
            <p className="text-sm text-blue-700">
              Based on your activity, here's how to get the most from GradedWash:
            </p>
          </div>
        </div>
        
        <div className="mt-4 space-y-3">
          {!userBehavior.personalityTestCompleted && (
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg">
              <div className="text-xl">🧠</div>
              <div className="flex-1">
                <h5 className="font-medium text-sm">Complete Your Personality Test</h5>
                <p className="text-xs text-gray-600">Get better matches and recommendations</p>
              </div>
              <button className="btn btn-primary btn-sm">Take Test</button>
            </div>
          )}
          
          {userBehavior.personalityTestCompleted && userBehavior.localMeetsViewed > 0 && (
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg">
              <div className="text-xl">📍</div>
              <div className="flex-1">
                <h5 className="font-medium text-sm">Join Local Meets</h5>
                <p className="text-xs text-gray-600">You've viewed {userBehavior.localMeetsViewed} meetups</p>
              </div>
              <PaymentGate feature="local-meets" userSubscriptionStatus={userSubscriptionStatus}>
                <button className="btn btn-primary btn-sm">Upgrade to Join</button>
              </PaymentGate>
            </div>
          )}
          
          {userBehavior.messagesAttempted > 0 && (
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg">
              <div className="text-xl">💬</div>
              <div className="flex-1">
                <h5 className="font-medium text-sm">Unlock Messaging</h5>
                <p className="text-xs text-gray-600">Connect with travelers you're interested in</p>
              </div>
              <PaymentGate feature="messages" userSubscriptionStatus={userSubscriptionStatus}>
                <button className="btn btn-primary btn-sm">Upgrade to Message</button>
              </PaymentGate>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSafetyGuide = () => {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
            <span className="text-white text-sm">🛡️</span>
          </div>
          <div>
            <h4 className="font-semibold text-yellow-900">Safety First!</h4>
            <p className="text-sm text-yellow-700">Always meet in public places for your first encounter</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <h5 className="font-medium text-yellow-900 mb-2">✅ Do's:</h5>
            <ul className="space-y-1 text-yellow-800">
              <li>• Meet in public, well-lit locations</li>
              <li>• Tell a friend where you're going</li>
              <li>• Trust your instincts</li>
              <li>• Use our personality matching system</li>
            </ul>
          </div>
          <div>
            <h5 className="font-medium text-yellow-900 mb-2">❌ Don'ts:</h5>
            <ul className="space-y-1 text-yellow-800">
              <li>• Meet in private or secluded areas</li>
              <li>• Share personal financial information</li>
              <li>• Ignore red flags</li>
              <li>• Rush into travel arrangements</li>
            </ul>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="ai-marketing-agent">
      {renderConversionMessage()}
      {renderBehavioralInsights()}
      {renderSafetyGuide()}
      
      {/* Personalized Offer Modal */}
      {showPersonalizedOffer && currentOffer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary-dark rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl">🎉</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {currentOffer.title}
              </h2>
              <p className="text-gray-600">
                {currentOffer.message}
              </p>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Exclusive Benefits:</h3>
              <ul className="space-y-2">
                {currentOffer.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-success mr-2">✓</span>
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gradient-to-r from-primary to-primary-dark rounded-lg p-4 mb-6">
              <p className="text-white text-sm font-medium">{currentOffer.urgency}</p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowPersonalizedOffer(false)}
                className="btn btn-secondary flex-1"
              >
                Maybe Later
              </button>
              <button
                onClick={() => {
                  setShowPersonalizedOffer(false);
                  window.location.href = '/pricing';
                }}
                className="btn btn-primary flex-1"
              >
                {currentOffer.cta}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 